import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MessageCircle, Pencil, Trash2, ChevronLeft, ChevronRight, UserX, Loader2 } from 'lucide-react';
import { useContacts, useContactCategories } from '../hooks/useContacts';
import { useDeleteContact } from '../hooks/useContactMutations';
import { ContactBadge, EngagementBar } from './ContactBadge';
import type { Contact, ContactFilters } from '../types';

interface ContactsTableProps {
  onEdit: (contact: Contact) => void;
}

export function ContactsTable({ onEdit }: ContactsTableProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<ContactFilters>({
    search: '',
    category: 'all',
    neighborhood: '',
    activeOnly: true,
  });

  const { contacts, total, isLoading } = useContacts(filters, page);
  const { data: categories = [], isLoading: categoriesLoading } = useContactCategories();
  const deleteMutation = useDeleteContact();

  const PAGE_SIZE = 50;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = (value: string) => {
    setFilters((f) => ({ ...f, search: value }));
    setPage(0);
  };

  const handleCategory = (cat: string) => {
    setFilters((f) => ({ ...f, category: cat }));
    setPage(0);
  };

  const handleDelete = (contact: Contact) => {
    if (!window.confirm(`Remover "${contact.full_name}" da base? A ação pode ser revertida.`)) return;
    deleteMutation.mutate(contact.id);
  };

  const openWhatsApp = (phone: string | null, name: string) => {
    if (!phone) return;
    const clean = phone.replace(/\D/g, '');
    const num = clean.startsWith('55') ? clean : `55${clean}`;
    const msg = encodeURIComponent(`Olá, ${name}!`);
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank', 'noopener');
  };

  const activeCategoryLabel =
    filters.category === 'all'
      ? null
      : categories.find((c) => c.slug === filters.category)?.label ?? filters.category;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Barra de busca ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle pointer-events-none" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Buscar por nome, telefone ou e-mail…"
            className="w-full rounded-xl bg-midnight-2/60 border border-hairline pl-9 pr-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 focus:bg-midnight-2 transition-all"
          />
        </div>

        <label className="flex items-center gap-2 text-xs text-ink-muted cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={filters.activeOnly}
            onChange={(e) => { setFilters((f) => ({ ...f, activeOnly: e.target.checked })); setPage(0); }}
            className="h-4 w-4 rounded accent-accent"
          />
          Apenas ativos
        </label>
      </div>

      {/* ── Chips de categoria (dinâmicos) ── */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleCategory('all')}
          className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-all cursor-pointer ${
            filters.category === 'all'
              ? 'border-accent/50 bg-accent/15 text-accent'
              : 'border-hairline bg-midnight-2/40 text-ink-muted hover:border-hairline-strong hover:text-ink'
          }`}
        >
          Todos
        </button>

        {categoriesLoading ? (
          <span className="text-xs text-ink-subtle animate-pulse px-2 py-1">Carregando…</span>
        ) : (
          categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategory(cat.slug)}
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-all cursor-pointer ${
                filters.category === cat.slug
                  ? 'border-accent/50 bg-accent/15 text-accent'
                  : 'border-hairline bg-midnight-2/40 text-ink-muted hover:border-hairline-strong hover:text-ink'
              }`}
            >
              {cat.label}
            </button>
          ))
        )}
      </div>

      {/* ── Tabela ── */}
      <div className="rounded-2xl border border-hairline overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-ink-muted gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Carregando contatos…</span>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-ink-muted gap-3">
            <UserX className="h-10 w-10 text-ink-subtle" />
            <p className="text-sm">Nenhum contato encontrado.</p>
            {filters.search && (
              <button className="text-xs text-accent underline cursor-pointer" onClick={() => handleSearch('')}>
                Limpar busca
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-midnight-2/30">
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">Nome</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden sm:table-cell">Categoria</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden md:table-cell">Telefone</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden lg:table-cell">Cidade / Bairro</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden xl:table-cell">Engajamento</th>
                  <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">Ações</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, i) => (
                  <tr
                    key={contact.id}
                    onClick={() => navigate(`/app/contatos/${contact.id}`)}
                    className={`border-b border-hairline/50 transition-colors cursor-pointer hover:bg-white/[0.03] ${
                      !contact.active ? 'opacity-50' : ''
                    } ${i % 2 === 0 ? '' : 'bg-midnight-2/10'}`}
                  >
                    {/* Nome */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-ink group-hover:text-accent transition-colors">
                          {contact.full_name}
                        </span>
                        {contact.email && (
                          <span className="text-xs text-ink-subtle hidden sm:block truncate max-w-[200px]">
                            {contact.email}
                          </span>
                        )}
                        <div className="sm:hidden mt-1">
                          <ContactBadge category={contact.category} categories={categories} />
                        </div>
                      </div>
                    </td>

                    {/* Categoria */}
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <ContactBadge category={contact.category} categories={categories} />
                      {contact.leader && (
                        <p className="text-[10px] text-ink-subtle mt-1 truncate max-w-[140px]">
                          ↳ {contact.leader.full_name}
                        </p>
                      )}
                    </td>

                    {/* Telefone */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      {contact.phone ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-ink-muted">{contact.phone}</span>
                          {contact.phone_whatsapp && (
                            <span className="text-[10px] text-emerald-400 font-semibold">WA</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-ink-subtle">—</span>
                      )}
                    </td>

                    {/* Cidade / Bairro */}
                    <td className="px-4 py-3 hidden lg:table-cell text-ink-muted text-xs">
                      {[contact.city, contact.neighborhood].filter(Boolean).join(' · ') || '—'}
                    </td>

                    {/* Engajamento */}
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <EngagementBar score={contact.engagement_score} />
                    </td>

                    {/* Ações */}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {contact.phone && contact.phone_whatsapp && (
                          <button
                            onClick={() => openWhatsApp(contact.phone, contact.full_name)}
                            title="Abrir WhatsApp"
                            className="rounded-lg p-1.5 text-emerald-400 hover:bg-emerald-400/10 transition-colors cursor-pointer"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onEdit(contact)}
                          title="Editar"
                          className="rounded-lg p-1.5 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(contact)}
                          title="Remover"
                          disabled={deleteMutation.isPending}
                          className="rounded-lg p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger transition-colors cursor-pointer disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Paginação ── */}
      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-ink-muted px-1">
          <span>
            {total} contato{total !== 1 ? 's' : ''}
            {activeCategoryLabel ? ` · ${activeCategoryLabel}` : ''}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-lg p-1.5 hover:bg-white/5 disabled:opacity-30 cursor-pointer disabled:cursor-default transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-mono">{page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-lg p-1.5 hover:bg-white/5 disabled:opacity-30 cursor-pointer disabled:cursor-default transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
